import { lazy, Suspense } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ThemeProvider } from '@/components/ThemeProvider'
import { BasemapProvider } from '@/components/BasemapProvider'
import { AuthProvider } from '@/components/AuthProvider'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { AdminProvider } from '@/components/AdminProvider'
import { AdminGuard } from '@/components/AdminGuard'
import { HomePage } from '@/pages/HomePage'
import { SignInPage } from '@/pages/SignInPage'
import { SignUpPage } from '@/pages/SignUpPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { AccountOverviewPage } from '@/pages/AccountOverviewPage'
import { AccountKeysPage } from '@/pages/AccountKeysPage'
import { AccountProfilePage } from '@/pages/AccountProfilePage'
import { AccountSecurityPage } from '@/pages/AccountSecurityPage'
import { AccountOrgPage } from '@/pages/AccountOrgPage'
import { InviteAcceptPage } from '@/pages/InviteAcceptPage'
import { ApiPage } from '@/pages/ApiPage'
import { ScrollToTop } from '@/components/site/ScrollToTop'
import { RouteSeo } from '@/components/RouteSeo'
import { AuthErrorBoundary } from '@/components/site/AuthErrorBoundary'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { SecurityPage } from '@/pages/SecurityPage'
import { AccessibilityPage } from '@/pages/AccessibilityPage'
import { TermsPage } from '@/pages/TermsPage'
import { DataPage } from '@/pages/DataPage'

// Redoc is heavy (~400 KB gzipped). Lazy-load so the bundle for every other
// route stays lean and only the /docs visitor pays.
const ReferencePage = lazy(() =>
  import('@/pages/ReferencePage').then((m) => ({ default: m.ReferencePage })),
)
// Interactive API playground (Swagger UI), account-only. ScalarPage is kept
// in the tree (no route) so it's easy to bring back for comparison.
const PlaygroundPage = lazy(() =>
  import('@/pages/PlaygroundPage').then((m) => ({ default: m.PlaygroundPage })),
)
const ReportingPage = lazy(() =>
  import('@/pages/ReportingPage').then((m) => ({ default: m.ReportingPage })),
)

// Admin pages: lazy so the /admin bundle never lands on anonymous visitors.
const AdminHomePage = lazy(() =>
  import('@/pages/admin/AdminHomePage').then((m) => ({ default: m.AdminHomePage })),
)
const AdminUsersPage = lazy(() =>
  import('@/pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminUserDetailPage = lazy(() =>
  import('@/pages/admin/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage })),
)

// /map — full-bleed enhanced map. Lazy-loaded so the homepage doesn't pull
// in the explorer's extra panels until someone navigates there.
const ExplorePage = lazy(() =>
  import('@/pages/ExplorePage').then((m) => ({ default: m.ExplorePage })),
)

export default function App() {
  return (
    <ThemeProvider>
      <BasemapProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RouteSeo />
          <AuthErrorBoundary />
          <AuthProvider>
            <AdminProvider>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/sign-in" element={<SignInPage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/api" element={<ApiPage />} />
              <Route
                path="/explore"
                element={
                  <Suspense fallback={null}>
                    <ExplorePage />
                  </Suspense>
                }
              />
              <Route
                path="/docs"
                element={
                  <Suspense fallback={null}>
                    <ReferencePage />
                  </Suspense>
                }
              />
              <Route
                path="/account/playground"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={null}>
                      <PlaygroundPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account/reporting"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={null}>
                      <ReportingPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <AccountOverviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account/keys"
                element={
                  <ProtectedRoute>
                    <AccountKeysPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account/profile"
                element={
                  <ProtectedRoute>
                    <AccountProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account/security"
                element={
                  <ProtectedRoute>
                    <AccountSecurityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminGuard>
                    <Suspense fallback={null}>
                      <AdminHomePage />
                    </Suspense>
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminGuard>
                    <Suspense fallback={null}>
                      <AdminUsersPage />
                    </Suspense>
                  </AdminGuard>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <AdminGuard>
                    <Suspense fallback={null}>
                      <AdminUserDetailPage />
                    </Suspense>
                  </AdminGuard>
                }
              />
              <Route
                path="/account/org"
                element={
                  <ProtectedRoute>
                    <AccountOrgPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account/org/invite/:token"
                element={<InviteAcceptPage />}
              />
              <Route path="/data"          element={<DataPage />} />
              <Route path="/privacy"       element={<PrivacyPage />} />
              <Route path="/security"      element={<SecurityPage />} />
              <Route path="/accessibility" element={<AccessibilityPage />} />
              <Route path="/terms"         element={<TermsPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </AdminProvider>
          </AuthProvider>
        </BrowserRouter>
      </BasemapProvider>
    </ThemeProvider>
  )
}
